import { c as _c } from "react/compiler-runtime";
import type { ResourceType } from '@repo/api-gw-sdk';
import { BackupPolicyType, Provider } from '@repo/api-gw-sdk';
import { useMemo } from 'react';
import { apps, sourceAccountId, dataClasses, environment, resourceName, resourceType, sensitivityAnnotations, sourceRegion, subnets, vpc, resourceId, cloudProvider, resourceGroupName, tagKeys, tagKeyValues, sourceAccountTagKeys, sourceAccountTagKeyValues, globalClusterIdentifier } from '@/components/queryBuilder/properties';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import type { FilterProperty } from '@/types/advanceFilter';
const baseProperties: FilterProperty[] = [resourceType, dataClasses, environment, apps];
const propertiesWithSensitivityAnnotations: FilterProperty[] = [...baseProperties, sensitivityAnnotations];
const sourceLocationProperties: FilterProperty[] = [cloudProvider, sourceAccountId, sourceRegion, vpc, subnets, resourceGroupName, sourceAccountTagKeys, sourceAccountTagKeyValues];
const sourceLocationPropertiesWithGlobalClusterIdentifier: FilterProperty[] = [...sourceLocationProperties, globalClusterIdentifier];
const resourceIdentifierProperties: FilterProperty[] = [resourceName, resourceId];
const tagProperties: FilterProperty[] = [tagKeys, tagKeyValues];
export const useAllBackupPolicyProperties = () => {
  const $ = _c(3);
  const {
    SensitivityAnnotations: enableSensitivityAnnotations,
    AuroraGlobalDbDiscoverySupport: enableAuroraGlobalDbDiscoverySupport
  } = useFeatureFlags();
  const t0 = enableSensitivityAnnotations ? propertiesWithSensitivityAnnotations : baseProperties;
  const t1 = enableAuroraGlobalDbDiscoverySupport ? sourceLocationPropertiesWithGlobalClusterIdentifier : sourceLocationProperties;
  let t2;
  if ($[0] !== t0 || $[1] !== t1) {
    t2 = [t0, t1, resourceIdentifierProperties, tagProperties];
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
const awsPolicySupportedProperties = new Set<FilterProperty>([resourceType, dataClasses, environment, apps, sensitivityAnnotations, sourceAccountId, sourceRegion, vpc, subnets, resourceName, resourceId, tagKeys, tagKeyValues, globalClusterIdentifier]);
const gcpPolicySupportedProperties = new Set<FilterProperty>([resourceType, dataClasses, environment, apps, sensitivityAnnotations, sourceAccountId, sourceRegion, resourceName, resourceId, tagKeys, tagKeyValues, sourceAccountTagKeys, sourceAccountTagKeyValues]);
const azurePolicySupportedProperties = new Set<FilterProperty>([resourceType, dataClasses, environment, apps, sensitivityAnnotations, sourceAccountId, sourceRegion, resourceGroupName, resourceName, resourceId, tagKeys, tagKeyValues]);
const cloudPropsMap: Record<Provider, Set<FilterProperty>> = {
  [Provider.Aws]: awsPolicySupportedProperties,
  [Provider.Gcp]: gcpPolicySupportedProperties,
  [Provider.Azure]: azurePolicySupportedProperties,
  [Provider.MongoAtlas]: new Set(),
  [Provider.GoogleWorkspace]: new Set(),
  [Provider.ProviderUnspecified]: new Set()
};

// Properties not supported by AWS Native Standard resources (EFS, FSX,
// DocumentDB, Neptune, Redshift): these resource types have no application
// detection, no data classification, no Azure MIP sensitivity annotations,
// and no Aurora global cluster identifier.
const awsNativeStandardUnsupportedProperties = new Set<FilterProperty>([apps, dataClasses, sensitivityAnnotations, globalClusterIdentifier]);
const azureFilesUnsupportedProperties = new Set<FilterProperty>([apps, dataClasses, sensitivityAnnotations]);
export const useBackupPolicySupportedProperties = (supportedResourceTypes, sourceProviders, policyType) => {
  const $ = _c(9);
  const properties = useAllBackupPolicyProperties();
  let t0;
  bb0: {
    if (!sourceProviders?.length) {
      t0 = properties;
      break bb0;
    }
    let t1;
    if ($[0] !== policyType || $[1] !== properties || $[2] !== sourceProviders || $[3] !== supportedResourceTypes) {
      let t2;
      if ($[5] !== policyType || $[6] !== sourceProviders || $[7] !== supportedResourceTypes) {
        t2 = group => group.filter(prop => {
          if (policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AWS_NATIVE_STANDARD && awsNativeStandardUnsupportedProperties.has(prop)) {
            return false;
          }
          if (policyType === BackupPolicyType.BACKUP_POLICY_TYPE_AZURE_FILES && azureFilesUnsupportedProperties.has(prop)) {
            return false;
          }
          return sourceProviders.some(cloud => {
            if (prop.name === resourceType.name) {
              return supportedResourceTypes.length > 1;
            } else {
              if (prop.name === cloudProvider.name) {
                return sourceProviders.length > 1;
              } else {
                return cloudPropsMap[cloud].has(prop);
              }
            }
          });
        });
        $[5] = policyType;
        $[6] = sourceProviders;
        $[7] = supportedResourceTypes;
        $[8] = t2;
      } else {
        t2 = $[8];
      }
      t1 = properties.map(t2);
      $[0] = policyType;
      $[1] = properties;
      $[2] = sourceProviders;
      $[3] = supportedResourceTypes;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    t0 = t1;
  }
  return t0;
};